import type { WordListEntry, WordListScanResult } from '@/teoCore/TeoApi';
import { TeoEventTarget } from '@/teoCore/utility/TeoEventTarget';

import type { TeoText } from '../TeoText';
import type { WordListHandler } from './WordListHandler';
import { WordListSubHandler } from './WordListSubHandler';

export type BrandFitHandlerEvents = {
  brandFitCheck: (pass: boolean | undefined) => void;
};
export const BrandFitCategories = ['replacements', 'checks'] as const;
export type BrandFitCategory = (typeof BrandFitCategories)[number];

export class BrandFitHandler extends WordListSubHandler {
  passed: boolean | undefined;
  loadingMessages: string[] = [
    'Initialisiere Text-Scan.',
    'Strukturiere Text-Performance-KPI-Analyse.',
    'Initialisiere linguistische Muster-Erkennung.',
    'Text-Performance-KPI Analyse abgeschlossen.',
    'Initialisiere Text-Performance-KI.',
    'Justiere Kanal-Parameter zur Tiefen-Analyse.',
    'Analyse-Validierung in Bearbeitung.',
    'Lade Corporate-Regel-Sets.',
    'Starte Corporate-Scan.',
    'Plausibilität-Check gestartet.',
    'Validiere Anomalien des Corporate-Scans.',
  ];
  constructor(wordLists: WordListHandler, text: TeoText) {
    super(
      wordLists,
      text,
      new TeoEventTarget<BrandFitHandlerEvents>(),
      'brand-scanner',
      'brandFitWordList',
      'brandFitCategory',
    );
    this.relationOffset = 200000;
    this.events.addEventListener('gotScanResult', (result) => {
      if (this.text.core.dynamicConfig.options.brandFitEnabled) {
        this.handleScanResult(result);
      }
    });
    this.text.ui.events.addEventListener('view', (viewName: string) => {
      if (
        viewName === 'brand-scanner' &&
        this.text.analysisState.canAnalyze &&
        !this.text.analysisState.brandFitUpToDate
      ) {
        this.text.analyze({ trigger: null });
      }
    });
  }

  override async scan(): Promise<void> {
    await super.scan('brand-scanner');
  }

  private handleScanResult = (result: WordListScanResult): void => {
    this.lists = result.word_lists.filter(
      (wordList) => wordList.use_for_ct && wordList.relation_type !== 0,
    );

    this.checkBrandFit();
    this.syncCurrentEntries();
  };

  private checkBrandFit(): void {
    if (!this.lists || this.lists.length === 0) {
      this.passed = undefined;
      this.events.triggerEvent('brandFitCheck', this.passed);
      return;
    }

    const notPassed = this.lists.some((wordList) =>
      wordList.current_text_entries.some(entryHasExpressionWithPriorityZero),
    );

    this.passed = !notPassed;
    this.events.triggerEvent('brandFitCheck', this.passed);
  }
}

function entryHasExpressionWithPriorityZero(entry: WordListEntry): boolean {
  return entry.expressions.find((expression) => expression.id == entry.found_by)?.priority === 0;
}
